import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { Pressable } from 'react-native';

import { HStack, Text, VStack } from '@squire/ui';

import {
  BottomSheet,
  type BottomSheetRef,
} from '@/components/_shared/BottomSheet';
import { CloseButton } from '@/components/_shared/CloseButton';
import { useCancelOrderMutation } from '@/hooks/mutations/appointment/useCancelOrderMutation';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type { CancelOrderModalProps } from './types';

export const CancelOrderModal = forwardRef<
  BottomSheetRef,
  CancelOrderModalProps
>(({ saleOrderId, appointmentIds, onKeep, onClose, onSuccess }, ref) => {
  const { t } = useScreenTranslations('CancelOrderModal');
  const bottomSheetRef = useRef<BottomSheetRef>(null);

  useImperativeHandle(ref, () => ({
    open: (animated?: boolean) => bottomSheetRef.current?.open(animated),
    close: (animated?: boolean) => bottomSheetRef.current?.close(animated),
    snapTo: (index: number, animated?: boolean) =>
      bottomSheetRef.current?.snapTo(index, animated),
    snapToIndex: (index: number, animated?: boolean) =>
      bottomSheetRef.current?.snapToIndex(index, animated),
  }));

  const cancelOrderMutation = useCancelOrderMutation({
    onSuccess: () => {
      bottomSheetRef.current?.close();
      onSuccess?.();
    },
  });

  const handleConfirm = () => {
    cancelOrderMutation.mutate({
      saleOrderId,
      appointmentIds,
      sendNotifications: true,
    });
  };

  const handleKeep = () => {
    bottomSheetRef.current?.close();
    onKeep?.();
  };

  const handleClose = () => {
    bottomSheetRef.current?.close();
    onClose?.();
  };

  return (
    <BottomSheet ref={bottomSheetRef} onDismiss={onClose} dismissible>
      <VStack style={styles.content}>
        {/* Header */}
        <HStack style={styles.header}>
          <VStack style={styles.titleContainer}>
            <Text variant="3xlStrong" color="textPrimary">
              {t('title')}
            </Text>
          </VStack>
          <CloseButton
            onPress={handleClose}
            accessibilityLabel={t('accessibility.close')}
            accessibilityHint={t('accessibility.closeHint')}
          />
        </HStack>

        {/* Message */}
        <Text variant="l" color="textPrimary" style={styles.message}>
          {t('message')}
        </Text>

        {/* Buttons */}
        <VStack style={styles.buttonsContainer}>
          {/* Cancel Button - Destructive */}
          <Pressable
            onPress={handleConfirm}
            disabled={cancelOrderMutation.isPending}
            style={({ pressed }) => [
              styles.cancelButton,
              (pressed || cancelOrderMutation.isPending) && { opacity: 0.7 },
            ]}
            accessibilityRole="button"
            accessibilityLabel={t('accessibility.cancel')}
            aria-disabled={cancelOrderMutation.isPending || undefined}
          >
            <Text variant="mStrong" color="textPrimaryInverse">
              {cancelOrderMutation.isPending ? t('canceling') : t('cancel')}
            </Text>
          </Pressable>

          {/* Keep Button - Secondary */}
          <Pressable
            onPress={handleKeep}
            style={({ pressed }) => [
              styles.keepButton,
              pressed && { opacity: 0.7 },
            ]}
            accessibilityRole="button"
            accessibilityLabel={t('accessibility.keep')}
          >
            <Text variant="mStrong" color="textPrimary">
              {t('keep')}
            </Text>
          </Pressable>
        </VStack>
      </VStack>
    </BottomSheet>
  );
});

CancelOrderModal.displayName = 'CancelOrderModal';
